/* eslint-disable react/jsx-no-bind */
import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MaterialIcon } from '@dsny/dsny-component-library'
import {
  AIFilled,
  ExplicitFilled,
  PlayArrowFilled
} from '@dsny/dsny-component-library/icons'
import { formatSeconds } from 'utils/stringHelpers'
import { PLAY_TRIGGERS } from 'constants/tracking'
import dropdownStyles from 'components/Dropdowns/Dropdowns.module.scss'
import {
  QuickViewGhostButton,
  QuickViewTrackArtist,
  QuickViewTrackArtistRow,
  QuickViewTrackDownloadPill,
  QuickViewTrackDownloads,
  QuickViewTrackDuration,
  QuickViewTrackIndex,
  QuickViewTrackLeadingCell,
  QuickViewTrackPlayButton,
  QuickViewTrackRowContainer,
  QuickViewTrackText,
  QuickViewTrackTitle
} from './QuickViewModal.styles'

const menuContentClassName = [
  dropdownStyles.DropdownMenuContent,
  dropdownStyles.ReleaseInfoActionsDropdown
].join(' ')

const menuItemClassName = [
  dropdownStyles.DropdownMenuItem,
  dropdownStyles.ReleaseInfoActionsItem
].join(' ')

export default function QuickViewTrackRow({
  song,
  index,
  playlists,
  responsiveBreakpointsV2,
  onPlay,
  onPlayNext,
  onPlayLater,
  onAddToPlaylist,
  onCreatePlaylist,
  onDownload
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const hasAudioDownload = song?.assettype !== 'video' && song?.candownload
  const hasVideoDownload = song?.assettype === 'video' && song?.candownload
  const moreActionsLabel = [t('general.more-actions'), song?.title || '']
    .join(' ')
    .trim()
  const playLabel = [t('general.play'), song?.title || ''].join(' ').trim()

  return (
    <QuickViewTrackRowContainer
      $responsiveBreakpointsV2={responsiveBreakpointsV2}
      onDoubleClick={() => onPlay(song, index)}>
      <QuickViewTrackLeadingCell>
        <QuickViewTrackIndex>{index + 1}</QuickViewTrackIndex>
        <QuickViewTrackPlayButton
          aria-label={playLabel}
          type="button"
          onClick={() => onPlay(song, index)}>
          <PlayArrowFilled
            width={16}
            height={16}
            color={theme.colors.neutralW100} />
        </QuickViewTrackPlayButton>
      </QuickViewTrackLeadingCell>

      <QuickViewTrackText>
        <QuickViewTrackTitle>{song?.title}</QuickViewTrackTitle>
        <QuickViewTrackArtistRow>
          {song?.explicit && (
            <ExplicitFilled width={16} height={16} color={theme.colors.neutralW90} />
          )}
          {song?.is_ai_generated && (
            <AIFilled width={16} height={16} color={theme.colors.neutralW90} />
          )}
          <QuickViewTrackArtist>{song?.artist}</QuickViewTrackArtist>
        </QuickViewTrackArtistRow>
      </QuickViewTrackText>

      <QuickViewTrackDuration
        $responsiveBreakpointsV2={responsiveBreakpointsV2}>
        {song?.duration ? formatSeconds(song.duration) : ''}
      </QuickViewTrackDuration>

      <QuickViewTrackDownloads
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        onDoubleClick={(event) => event.stopPropagation()}>
        {hasAudioDownload && (
          <>
            <QuickViewTrackDownloadPill
              label="Download WAV"
              mode="dark"
              onClick={() => onDownload(song, 'wav')} />
            <QuickViewTrackDownloadPill
              label="MP3"
              mode="dark"
              onClick={() => onDownload(song, 'mp3')} />
          </>
        )}
        {hasVideoDownload && (
          <QuickViewTrackDownloadPill
            label="Download"
            mode="dark"
            onClick={() => onDownload(song)} />
        )}
      </QuickViewTrackDownloads>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <QuickViewGhostButton
            aria-label={moreActionsLabel}
            type="button"
            onDoubleClick={(event) => event.stopPropagation()}>
            <MaterialIcon name="MoreVertical" color={theme.colors.neutralW90} />
          </QuickViewGhostButton>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            avoidCollisions
            sideOffset={8}
            style={{
              background: theme.colors.neutralW20,
              color: theme.colors.neutralW100
            }}
            className={menuContentClassName}>
            <DropdownMenu.Item
              className={menuItemClassName}
              onClick={() => onPlay(song, index, PLAY_TRIGGERS.TRACK_MENU)}>
              {t('general.play')}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className={menuItemClassName}
              onClick={() => onPlayNext(song)}>
              {t('general.play-next')}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className={menuItemClassName}
              onClick={() => onPlayLater(song)}>
              {t('general.play-later')}
            </DropdownMenu.Item>
            {song?.assettype === 'audio' && (
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger className={menuItemClassName}>
                  {t('general.addto')}
                  <div className={dropdownStyles.RightSlot}>
                    <MaterialIcon
                      name="KeyboardArrowRight"
                      color={theme.colors.neutralW100} />
                  </div>
                </DropdownMenu.SubTrigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent
                    avoidCollisions
                    sideOffset={2}
                    alignOffset={-5}
                    style={{
                      background: theme.colors.neutralW20,
                      color: theme.colors.neutralW100
                    }}
                    className={menuContentClassName}>
                    {playlists?.map((playlist) => (
                      <DropdownMenu.Item
                        key={playlist && playlist.playlistid}
                        className={menuItemClassName}
                        onClick={() => onAddToPlaylist(playlist, song, index)}>
                        {playlist && playlist.name}
                      </DropdownMenu.Item>
                    ))}
                    <DropdownMenu.Item
                      className={menuItemClassName}
                      onClick={() => onCreatePlaylist(song)}>
                      New playlist
                    </DropdownMenu.Item>
                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>
            )}
            <DropdownMenu.Arrow
              className={dropdownStyles.DropdownMenuArrow}
              style={{ fill: theme.colors.neutralW20 }} />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </QuickViewTrackRowContainer>
  )
}

QuickViewTrackRow.propTypes = {
  song: PropTypes.object,
  index: PropTypes.number,
  playlists: PropTypes.arrayOf(PropTypes.object),
  responsiveBreakpointsV2: PropTypes.bool,
  onPlay: PropTypes.func,
  onPlayNext: PropTypes.func,
  onPlayLater: PropTypes.func,
  onAddToPlaylist: PropTypes.func,
  onCreatePlaylist: PropTypes.func,
  onDownload: PropTypes.func
}
